import {
  Button,
  Col,
  Form,
  FormInstance,
  Input,
  PageTitle,
  PriorityTag,
  Row,
  Table,
  Tag,
  Tooltip,
} from 'components/index';
import moment from 'moment';
import { AllEntityTypes } from 'models/Task';
import React, { useEffect, useRef } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { LoadingOutlined } from '@ant-design/icons';
import { useTasks } from 'components/common/tasks/useTasks';
import { StatusBadge } from 'components/common/StatusBadge';
import { DeleteIcon, FilterIcon, PlusIcon, ViewIcon } from 'components/common/icons';
import { DeleteModal, Filters, StatusAnalytics } from 'components/common/tasks/index';
import styles from './styles.module.scss';

const { Search } = Input;

export interface TaskDataType {
  id: string;
  uuid: number | string;
  name: string;
  summary: string;
  assignedTo: string;
  priorityLevel: string;
  entity: string;
  status: string;
  createdBy: string;
  dueDate: string;
  createDate: string;
  lastModifiedDate: string;
}

export interface TasksProps {
  entityType?: string;
  entityId?: string;
  isMainTable?: boolean;
  hasAccessToWrite: boolean;
  hasAccessToDelete: boolean;
  showAnalytics?: boolean;
  forDashboard?: boolean;
  onTaskAdd?: () => void;
  onRedirectToTask?: (id: string) => void;
  onTotalUpdate?: (total: number) => void;
}

export const Tasks: React.FC<TasksProps> = (props) => {
  const {
    entityType,
    entityId,
    hasAccessToDelete,
    hasAccessToWrite,
    isMainTable,
    showAnalytics = true,
    forDashboard,
    onTaskAdd,
    onRedirectToTask,
    onTotalUpdate,
  } = props;
  const [filtersForm] = Form.useForm<FormInstance>();
  const {
    onCloseFilters,
    onOpenFilters,
    openFilters,
    handleTableChange,
    routeChange,
    isLoading,
    pagination,
    onSearch,
    data,
    total,
    onAdd,
    applyFilters,
    resetFilters,
    onEntityClick,
    onDeleteModalStateChange,
    deleteItem,
  } = useTasks({ entityType, entityId, isMainTable, forDashboard });

  // Track the previous total to avoid unnecessary updates
  const previousTotalRef = useRef<number | undefined>(undefined);

  // Report total count back to parent dashboard component
  useEffect(() => {
    if (forDashboard && onTotalUpdate && total !== undefined && previousTotalRef.current !== total) {
      // Update the ref to track the latest value
      previousTotalRef.current = total;
      onTotalUpdate(total);
    }
  }, [forDashboard, onTotalUpdate, total]);

  const columns: ColumnsType<TaskDataType> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      render: (_, { uuid }) => {
        return uuid;
      },
    },
    {
      title: 'Summary',
      dataIndex: 'summary',
      key: 'summary',
      sorter: true,
      width: 200, // Set max width
      ellipsis: true, // Enable ellipsis
      render: (value) => {
        return (
          <Tooltip title={value}>
            <div className={'text-ellipsis'}>{value}</div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Assigned To',
      dataIndex: '_assignedTo',
      key: 'assignedTo',
      sorter: false,
      width: 200, // Set max width
      ellipsis: true, // Enable ellipsis
      render: (value) => {
        return value ? (
          <Tooltip title={`${value?.firstName} ${value?.lastName}`}>
            <div className={'text-ellipsis'}>
              {value?.firstName} {value?.lastName}
            </div>
          </Tooltip>
        ) : (
          ''
        );
      },
    },
    {
      title: 'Priority',
      dataIndex: 'priorityLevel',
      key: 'priorityLevel',
      sorter: true,
      width: 200, // Set max width
      ellipsis: true, // Enable ellipsis
      render: (value) => {
        return <PriorityTag type={value} />;
      },
    },
    {
      title: 'Entity',
      key: 'entityType',
      sorter: !entityType,
      width: 200, // Set max width
      ellipsis: true, // Enable ellipsis
      render: (item) => {
        const entityType = AllEntityTypes[item.entityType]?.label;
        return entityType ? (
          <Tag
            title={`${entityType}:${item?.entityValue || item?.entityUuid || ''}`}
            onClick={item?.entityId ? () => onEntityClick(item.entityType, item.entityId) : undefined}
            className={styles.entity_tag}
            color={'#E1E4EA'}
          >
            {`${entityType}:${item?.entityValue || item?.entityUuid || ''}`}
          </Tag>
        ) : (
          ''
        );
      },
    },
    {
      title: 'Created By',
      dataIndex: '_createdBy',
      key: 'createdBy',
      sorter: false,
      width: 200, // Set max width
      ellipsis: true, // Enable ellipsis
      render: (value) => {
        return value ? (
          <Tooltip title={`${value?.firstName} ${value?.lastName}`}>
            <div className={'text-ellipsis'}>
              {value?.firstName} {value?.lastName}
            </div>
          </Tooltip>
        ) : (
          ''
        );
      },
    },
    {
      title: 'Created Date',
      dataIndex: 'createDate',
      key: 'createDate',
      render: (value) => moment(value).format('DD.MM.YYYY'),
      minWidth: 150,
      sorter: true,
    },
    {
      title: 'Last Updated Date',
      dataIndex: 'lastModifiedDate',
      key: 'lastModifiedDate',
      render: (value) => moment(value).format('DD.MM.YYYY'),
      minWidth: 180,
      sorter: true,
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      render: (value) => moment(value).format('DD.MM.YYYY HH:mm'),
      key: 'dueDate',
      minWidth: 150,
      sorter: true,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      minWidth: 150,
      render: (value) => {
        return value ? <StatusBadge type={value} /> : '';
      },
      sorter: true,
    },
    {
      fixed: 'right',
      width: 68,
      key: 'actions',
      render: (value) => (
        <Row wrap={false} align={'middle'} justify={'center'}>
          <Button
            onClick={() => {
              if (onRedirectToTask) {
                onRedirectToTask(value.id);
              } else {
                routeChange(value.id);
              }
            }}
            type={'text'}
            size={'small'}
            icon={<ViewIcon />}
          />
          {hasAccessToDelete && (
            <Button
              onClick={() => onDeleteModalStateChange({ name: value.name, id: value.id })}
              type={'text'}
              size={'small'}
              icon={<DeleteIcon />}
            />
          )}
        </Row>
      ),
    },
  ];

  return (
    <Col span={24}>
      <Row gutter={[0, 24]}>
        <Col span={24}>
          <Row align={'middle'} justify={'space-between'}>
            <PageTitle title={'Tasks'} />
            {hasAccessToWrite && !forDashboard && (
              <Button type={'primary'} onClick={onTaskAdd || onAdd} icon={<PlusIcon color={'#FFFFFF'} />}>
                Add New
              </Button>
            )}
          </Row>
        </Col>
        {showAnalytics && (
          <Col span={24}>
            <StatusAnalytics />
          </Col>
        )}
        <Col span={24} id={'tasks_list'} className={styles.main_container}>
          <Row>
            <Col span={24}>
              <Row justify={'end'} className={styles.actions_container}>
                <Col style={{ width: '300px' }}>
                  <Search enterButton={false} placeholder={'Search'} onChange={(e) => onSearch(e.target.value)} />
                </Col>
                <Button color="default" variant="filled" onClick={onOpenFilters} icon={<FilterIcon />} />
              </Row>
            </Col>
            <Col span={24} style={{ paddingTop: '20px' }}>
              <Table
                tableLayout={'auto'}
                bordered
                scroll={{
                  x: true,
                }}
                rowKey="id"
                size={'small'}
                dataSource={data}
                columns={columns as any}
                loading={{
                  indicator: <LoadingOutlined style={{ fontSize: 48 }} spin />,
                  spinning: isLoading,
                }}
                locale={
                  isLoading
                    ? { emptyText: null } // Hide "No Data" text while loading
                    : {} // Use default AntD "No Data" icon when empty
                }
                onChange={handleTableChange}
                pagination={{
                  position: ['bottomCenter'],
                  showSizeChanger: true,
                  showTotal: (total) => `Total ${total} items`,
                  size: 'default',
                  current: pagination.page,
                  pageSize: pagination.size,
                  total: total,
                }}
              ></Table>
            </Col>
          </Row>
        </Col>
      </Row>
      <DeleteModal openModal={!!deleteItem} onDeleteModalClose={onDeleteModalStateChange} dataToDelete={deleteItem} />
      <Filters
        form={filtersForm}
        drawerOpen={openFilters}
        closeDrawer={onCloseFilters}
        applyFilters={applyFilters}
        resetFilters={resetFilters}
        entityType={entityType}
      />
    </Col>
  );
};
